import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Text, Button, makeStyles, tokens } from '@fluentui/react-components';
import { HomeRegular, ArrowLeftRegular } from '@fluentui/react-icons';
import DrillDownView from '../DrillDownView';
import IssueList from '../IssueList';
import Breadcrumb from '../Breadcrumb';
import { fetchIssues } from '../../services/api';
import { Issue, DrillDownItem, BreadcrumbItem } from '../../types';

interface SubcategoryViewProps {
  apiUrl?: string;
  onNavigate?: (path: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    height: 'calc(100vh - 2rem)',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
  },
  leftColumn: {
    width: '68%',
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: tokens.spacingVerticalL,
    overflowY: 'auto',
  },
  rightColumn: {
    width: '32%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: tokens.spacingVerticalM,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
    gap: tokens.spacingVerticalM,
  },
});

const SubcategoryView: React.FC<SubcategoryViewProps> = ({ apiUrl, onNavigate }) => {
  const { domain, category, subcategory } = useParams<{ 
    domain: string; 
    category: string; 
    subcategory: string;
  }>();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const styles = useStyles();

  useEffect(() => {
    const loadIssues = async (): Promise<void> => {
      try {
        const data = await fetchIssues();
        setIssues(data);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load issues:', error);
        setLoading(false);
      }
    };
    loadIssues();
  }, []);

  const issueList = useMemo<DrillDownItem[]>(() => {
    if (!domain || !category || !subcategory) return [];
    const issueSet = new Set<string>();
    issues
      .filter(
        (issue) =>
          issue.Taxonomy.Domain === decodeURIComponent(domain) &&
          issue.Taxonomy.Category === decodeURIComponent(category) &&
          issue.Taxonomy.Subcategory === decodeURIComponent(subcategory)
      )
      .forEach((issue) => {
        issueSet.add(issue.Taxonomy.Issue);
      });
    return Array.from(issueSet)
      .map((issue) => ({
        label: issue,
        value: issue,
        count: issues.filter(
          (i) =>
            i.Taxonomy.Domain === decodeURIComponent(domain) &&
            i.Taxonomy.Category === decodeURIComponent(category) &&
            i.Taxonomy.Subcategory === decodeURIComponent(subcategory) &&
            i.Taxonomy.Issue === issue
        ).length,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [issues, domain, category, subcategory]);

  const handleIssueClick = (issue: DrillDownItem): void => {
    if (!domain || !category || !subcategory) return;
    const path = `/${encodeURIComponent(domain)}/${encodeURIComponent(category)}/${encodeURIComponent(subcategory)}/${encodeURIComponent(issue.value)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleBack = (): void => {
    if (!domain || !category) return;
    const path = `/${encodeURIComponent(domain)}/${encodeURIComponent(category)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleReset = (): void => {
    if (onNavigate) {
      onNavigate('/');
    } else {
      navigate('/');
    }
  };

  const handleIssueCardClick = (issue: Issue): void => {
    const path = `/${encodeURIComponent(issue.Taxonomy.Domain)}/${encodeURIComponent(issue.Taxonomy.Category)}/${encodeURIComponent(issue.Taxonomy.Subcategory)}/${encodeURIComponent(issue.Taxonomy.Issue)}/${encodeURIComponent(issue.Path)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const breadcrumbPath: BreadcrumbItem[] =
    domain && category && subcategory
      ? [
          { label: decodeURIComponent(domain), value: domain },
          { label: decodeURIComponent(category), value: category },
          { label: decodeURIComponent(subcategory), value: subcategory },
        ]
      : [];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Text>Loading issues...</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* LEFT COLUMN - Navigation */}
      <div className={styles.leftColumn}>
        <Breadcrumb path={breadcrumbPath} />
        <div className={styles.headerRow}>
          <Text size={500} weight="semibold">
            {subcategory ? `Issues in ${decodeURIComponent(subcategory)}` : 'Issues'}
          </Text>
          <div style={{ display: 'flex', gap: tokens.spacingVerticalXS }}>
            <Button
              icon={<ArrowLeftRegular />}
              appearance="subtle"
              onClick={handleBack}
              size="small"
            >
              Back
            </Button>
            <Button
              icon={<HomeRegular />}
              appearance="subtle"
              onClick={handleReset}
              size="small"
            >
              Reset
            </Button>
          </div>
        </div>
        <DrillDownView
          items={issueList}
          onItemClick={handleIssueClick}
        />
      </div>

      {/* RIGHT COLUMN - Issue List */}
      <div className={styles.rightColumn}>
        <IssueList
          issues={issues}
          currentPath={domain && category && subcategory ? { domain, category, subcategory } : undefined}
          onIssueClick={handleIssueCardClick}
        />
      </div>
    </div>
  );
};

export default SubcategoryView;
